import './ThankYouMAO.css';
import React, { useLayoutEffect, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useScrollToTop } from '../../lib/hooks';
import Disclaimer from '../Disclaimer/Disclaimer';
import {
  useAppContext,
  useFormDataContext,
  useLendersContext,
} from '../../lib/contexts';

const addParam = (params, key, value) => {
  if (value !== undefined && value !== null && String(value).trim() !== '') {
    params.set(key, String(value).trim());
  }
};

function getMaoUrlWithParams(data, appContext) {
  const params = new URLSearchParams();

  addParam(params, 'ZipCode', data.zip);
  addParam(params, 'State', data.state);
  addParam(params, 'ProjectServiceType', 'Windows/Doors');
  addParam(params, 'ProjectTypesConcentration', '1 to 10 Windows');
  addParam(
    params,
    'ProjectScope',
    data.projecttype == 'replace' ? 'Replace New' : 'Repair Existing'
  );
  addParam(params, 'HomeOwner', data.homeowner === 'yes' ? 'Yes' : 'No');
  addParam(params, 'SourceID', process.env.REACT_APP_MAO_SOURCE_ID);
  addParam(params, 'ExtClickID', window.leadId);
  params.set('MediaChannel', '');

  return params.toString().replace(/\+/g, '%20');
}

let maoScriptPromise = null;

function ensurePreconnect(url) {
  try {
    const { origin } = new URL(url);
    const id = `preconnect-${origin}`;
    if (document.getElementById(id)) return;

    const link = document.createElement('link');
    link.id = id;
    link.rel = 'preconnect';
    link.href = origin;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  } catch (e) {
    // Ignore invalid URLs / URL constructor errors
  }
}

function ensurePreloadScript(url) {
  const id = `preload-${url}`;
  if (document.getElementById(id)) return;

  const link = document.createElement('link');
  link.id = id;
  link.rel = 'preload';
  link.as = 'script';
  link.href = url;
  link.crossOrigin = 'anonymous';
  document.head.appendChild(link);
}

function loadMaoScriptOnce({ scriptId, src }) {
  if (document.getElementById(scriptId)) return Promise.resolve();
  if (maoScriptPromise) return maoScriptPromise;

  maoScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'text/javascript';
    script.async = true;
    script.src = src;

    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error(`Failed to load MAO script: ${src}`));

    document.head.appendChild(script);
  });

  return maoScriptPromise;
}

function runPixelHtml(containerEl, html) {
  containerEl.innerHTML = '';
  if (!html) return;

  containerEl.innerHTML = html;

  const scripts = containerEl.querySelectorAll('script');
  scripts.forEach((oldScript) => {
    const newScript = document.createElement('script');

    Array.from(oldScript.attributes || []).forEach((attr) => {
      newScript.setAttribute(attr.name, attr.value);
    });

    if (oldScript.src) {
      newScript.async = true;
      newScript.defer = true;
    }

    if (oldScript.text) newScript.text = oldScript.text;

    oldScript.parentNode?.replaceChild(newScript, oldScript);
  });
}

export default function ThankYouMAO() {
  const { appContext } = useAppContext();
  const { formData } = useFormDataContext();
  const { pixel } = useLendersContext();

  const location = useLocation();
  const navigate = useNavigate();
  useScrollToTop();

  const stateData = location.state;
  const finalFormData = stateData || formData;

  const MAO_TOKEN = process.env.REACT_APP_MAO_TOKEN;
  const MAO_SCRIPT_BASE =
    process.env.REACT_APP_MAO_SCRIPT_BASE ||
    'https://cdn.myadoptimizer.com/maojsfiles/landingpagesjs';

  const scriptSrc = useMemo(() => {
    if (!MAO_TOKEN) return null;
    return `${MAO_SCRIPT_BASE}/${MAO_TOKEN}.js`;
  }, [MAO_SCRIPT_BASE, MAO_TOKEN]);

  const queryString = useMemo(() => {
    if (!finalFormData?.zip) return '';
    return getMaoUrlWithParams(finalFormData, appContext);
  }, [finalFormData, appContext]);

  const pixelDivRef = useRef(null);
  const didPixelInitRef = useRef(false);
  const didMaoInitRef = useRef(false);

  useEffect(() => {
    if (!stateData && (!formData || !formData.zip)) {
      navigate('/', { replace: true });
    }
  }, [stateData, formData, navigate]);

  useLayoutEffect(() => {
    if (didPixelInitRef.current) return;
    if (!pixelDivRef.current) return;
    runPixelHtml(pixelDivRef.current, pixel);
    didPixelInitRef.current = true;
  }, [pixel]);

  useLayoutEffect(() => {
    if (!scriptSrc) return;

    ensurePreconnect(scriptSrc);
    ensurePreloadScript(scriptSrc);

    if (didMaoInitRef.current) return;
    if (!finalFormData?.zip) return;

    didMaoInitRef.current = true;

    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}?${queryString}`
    );

    loadMaoScriptOnce({ scriptId: 'mao-windows-script', src: scriptSrc }).catch(
      (e) => console.error(e)
    );
  }, [scriptSrc, finalFormData, queryString]);

  if (!finalFormData?.zip) {
    return <div className="thank-you-alternative__container">Loading...</div>;
  }

  return (
    <div className="thank-you-page-wrapper">
      <div id="confirmation_qs_header">
        <div id="confirmation_qs_text">
          &nbsp;Congratulations, you&apos;ve been matched!
        </div>
      </div>
      <div className="thank-you-mao__container">
        <div
          className="thank-you-alternative-lenders__pixel"
          ref={pixelDivRef}
        />
        <div id="target" style={{ minHeight: '140px' }} />
      </div>
      <div className="disclaimer" style={{ width: '100%' }}>
        <Disclaimer />
      </div>
    </div>
  );
}
